import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Paper
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  People as PeopleIcon
} from '@mui/icons-material';
import axios from 'axios';
import AuthContext from '../../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const EmailPromotionDialog = ({ open, onClose, release }) => {
  const { user } = useContext(AuthContext);
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Email lists
  const [emailLists, setEmailLists] = useState([]);
  const [selectedLists, setSelectedLists] = useState([]);
  
  // Form data
  const [formData, setFormData] = useState({
    title: '',
    customMessage: ''
  });
  
  // Promotion data
  const [promotion, setPromotion] = useState(null);
  const [emailPreview, setEmailPreview] = useState(null);
  const [sendResults, setSendResults] = useState(null);

  const steps = [
    'Email Lists',
    'Compose Message',
    'Preview & Send'
  ];

  useEffect(() => {
    if (open && release) {
      fetchEmailLists();
      setFormData({
        title: `New Release: ${release.title}`,
        customMessage: `I'm excited to share my latest release "${release.title}" with you! This ${release.type} showcases my ${release.primaryGenre} sound and I think you'll really enjoy it.`
      });
    }
  }, [open, release]);

  const fetchEmailLists = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/email-lists`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailLists(response.data);
      
      // Auto-select default list if available
      const defaultList = response.data.find(list => list.isDefault);
      if (defaultList) {
        setSelectedLists([defaultList._id]);
      }
    } catch (err) {
      setError('Failed to load email lists');
    }
  };

  const createPromotion = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/email-promotions`, {
        title: formData.title,
        releaseId: release._id,
        emailListIds: selectedLists,
        customMessage: formData.customMessage
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setPromotion(response.data);
      setActiveStep(2);
      await fetchEmailPreview(response.data._id);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create promotion');
      setLoading(false);
    }
  };

  const fetchEmailPreview = async (promotionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/email-promotions/${promotionId}/preview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailPreview(response.data);
    } catch (err) {
      setError('Failed to load email preview');
    }
  };

  const sendPromotion = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const response = await axios.post(`${API_URL}/email-promotions/${promotion._id}/send`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setSendResults(response.data);
      setSuccess(`Email promotion sent! ${response.data.sentCount} emails sent successfully.`);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to send promotion');
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (activeStep === 0 && selectedLists.length === 0) {
      setError('Please select at least one email list');
      return;
    }
    if (activeStep === 1) {
      if (!formData.title || !formData.customMessage) {
        setError('Please fill in all required fields');
        return;
      }
      createPromotion();
      return;
    }
    setActiveStep((prevStep) => prevStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  const handleClose = () => {
    // Reset all state
    setActiveStep(0);
    setSelectedLists([]);
    setFormData({ title: '', customMessage: '' });
    setPromotion(null);
    setEmailPreview(null);
    setSendResults(null);
    setError('');
    setSuccess('');
    onClose();
  };

  const getTotalRecipients = () => {
    return emailLists
      .filter(list => selectedLists.includes(list._id))
      .reduce((total, list) => total + list.totalContacts, 0);
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Typography variant="body1" gutterBottom>
              Select the email lists you want to send this promotion to:
            </Typography>
            <FormControl fullWidth sx={{ mt: 2 }}>
              <InputLabel>Email Lists</InputLabel>
              <Select
                multiple
                value={selectedLists}
                onChange={(e) => setSelectedLists(e.target.value)}
                input={<OutlinedInput label="Email Lists" />}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => {
                      const list = emailLists.find(l => l._id === value);
                      return (
                        <Chip 
                          key={value} 
                          label={`${list?.name} (${list?.totalContacts})`} 
                          size="small" 
                        />
                      );
                    })}
                  </Box>
                )}
              >
                {emailLists.map((list) => (
                  <MenuItem key={list._id} value={list._id}>
                    <Box>
                      <Typography>{list.name}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {list.totalContacts} contacts
                      </Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            
            {selectedLists.length > 0 && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Total recipients: {getTotalRecipients()} contacts
              </Alert>
            )}
            
            {emailLists.length === 0 && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                No email lists found. You need to create email lists first before sending promotions.
              </Alert>
            )}
          </Box>
        );

      case 1:
        return (
          <Box>
            <TextField
              fullWidth
              label="Email Subject/Title"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Personal Message"
              value={formData.customMessage}
              onChange={(e) => setFormData({ ...formData, customMessage: e.target.value })}
              margin="normal"
              multiline
              rows={6}
              required
              helperText="This message will appear at the top of your promotional email"
            />
            
            <Paper sx={{ p: 2, mt: 2, bgcolor: 'background.default' }}>
              <Typography variant="subtitle2" gutterBottom>
                Release Information (automatically included):
              </Typography>
              <Typography variant="body2">
                • Title: {release?.title}<br/>
                • Genre: {release?.primaryGenre}<br/>
                • Release Date: {release?.releaseDate ? new Date(release.releaseDate).toLocaleDateString() : 'N/A'}<br/>
                • Artist: {user?.artistName || user?.username}<br/>
                • Promo link will be automatically generated
              </Typography>
            </Paper>
          </Box>
        );

      case 2:
        return (
          <Box>
            {emailPreview && (
              <Box>
                <Typography variant="h6" gutterBottom>
                  Email Preview
                </Typography>
                <Paper 
                  sx={{ 
                    p: 2, 
                    maxHeight: 400, 
                    overflow: 'auto',
                    border: '1px solid',
                    borderColor: 'divider'
                  }}
                >
                  <div dangerouslySetInnerHTML={{ 
                    __html: emailPreview.htmlContent.replace('{recipient_name}', '[Recipient Name]') 
                  }} />
                </Paper>
                
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Sending Summary
                  </Typography>
                  <List>
                    <ListItem>
                      <ListItemIcon>
                        <EmailIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Subject"
                        secondary={emailPreview.subject}
                      />
                    </ListItem>
                    <ListItem>
                      <ListItemIcon>
                        <PeopleIcon />
                      </ListItemIcon>
                      <ListItemText
                        primary="Recipients"
                        secondary={`${promotion?.stats?.totalRecipients || 0} contacts across ${selectedLists.length} list(s)`}
                      />
                    </ListItem>
                  </List>
                </Box>
                
                {!sendResults && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Review the email content above. Click "Send Promotion" to send to all recipients.
                  </Alert>
                )}
                
                {sendResults && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Promotion Sent Successfully!
                    </Typography>
                    <Typography variant="body2">
                      • {sendResults.sentCount} emails sent<br/>
                      • {sendResults.failedCount} emails failed<br/>
                      • Campaign: {promotion?.title}
                    </Typography>
                  </Alert>
                )}
              </Box>
            )}
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <EmailIcon />
          Send Email Promotion
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
            {success}
          </Alert>
        )}

        <Stepper activeStep={activeStep} orientation="vertical">
          {steps.map((label, index) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
              <StepContent>
                {getStepContent(index)}
                <Box sx={{ mt: 2 }}>
                  <Button
                    disabled={index === 0}
                    onClick={handleBack}
                    sx={{ mr: 1 }}
                  >
                    Back
                  </Button>
                  {index === steps.length - 1 ? (
                    !sendResults && (
                      <Button
                        variant="contained"
                        onClick={sendPromotion}
                        disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                      >
                        {loading ? 'Sending...' : 'Send Promotion'}
                      </Button>
                    )
                  ) : (
                    <Button
                      variant="contained"
                      onClick={handleNext}
                      disabled={loading}
                    >
                      {loading ? 'Creating...' : 'Continue'}
                    </Button>
                  )}
                </Box>
              </StepContent>
            </Step>
          ))}
        </Stepper>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>
          {sendResults ? 'Close' : 'Cancel'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EmailPromotionDialog;
